import React, { useState, useEffect, useRef } from 'react';
import { TouchableOpacity, View } from 'react-native'
import { Ionicons } from '@expo/vector-icons';

import api from "../../../../utils/api"
import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Modal from '../../../../components/Modal';
import LottieView from 'lottie-react-native';
import IconButton from '../../../../components/IconButton';
import Input from '../../../../components/Forms/Input';
import { Icon } from '../../../../components/Icon';
import { useForm } from 'react-hook-form';
import { UnitAutocomplete } from '../Fees/Fees/AddPaymentModal';
import useFormSubmit from '../../../../hooks/useFormSubmit';



export function useScanTag(panelId: string, communityId: string) {
  const [isScanning, setIsScanning] = useState(false);
  const [errorResult, setErrorResult] = useState();
  const [cardResult, setCardResult] = useState();

  function scanTag() {
    setIsScanning(true)
    setErrorResult(null)
    setCardResult(null)

    api.post(`/communities/${communityId}/access/control-panels/${panelId}/detect-tag`)
      .then(({ data }) => {
        console.log("scanTag result", data);
        if (data.card) {
          setCardResult(data)
        } else {
          setErrorResult("No se encontró ningún tag")
        }
      })
      .catch(({ response }) => {
        alert("Error al escanear tag")
        console.log("scanTag Error", response.data);
      })
      .finally(() => {
        setIsScanning(false)
      })
  }


  return {
    isScanning,
    errorResult,
    cardResult,
    scanTag,
    resetScan: () => { setCardResult(null); setErrorResult(null); },
  }
}


function detectTagType(code: string): 'vehicle_tag' | 'rfid_keychain' | null {
  if (!code) return null
  if (/^\d+,\d+$/.test(code)) return 'vehicle_tag'
  if (/^\d{7,}$/.test(code)) return code.length >= 9 ? 'vehicle_tag' : 'rfid_keychain'
  return null
}


export default function AddTagModal({ panel, communityId, onRequestClose, onTagCreated, onAccessTagScanned }) {
  const { isScanning, errorResult, cardResult, scanTag, resetScan } = useScanTag(panel.uuid, communityId)
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: { type: 'vehicle_tag', name: '', tag_code: '' },
  })
  const [selectedUnit, setSelectedUnit] = useState();
  const [duplicate, setDuplicate] = useState(null);
  const lastAutoNameRef = useRef('')

  const tagCode = watch('tag_code')
  const currentName = watch('name')
  const tagType = watch('type')

  const typeOptions = [
    { value: 'vehicle_tag', label: 'Vehículo', icon: 'tagVehicular' },
    { value: 'rfid_keychain', label: 'Llavero', icon: 'tagLlavero' },
  ]

  useEffect(() => {
    if (!cardResult?.card?.card_code) return

    if (cardResult.access_tag) {
      onAccessTagScanned?.(cardResult.access_tag)
      return
    }

    const scannedCode = cardResult.card.card_code
    setValue('tag_code', scannedCode)
    const detected = detectTagType(scannedCode)
    if (detected) setValue('type', detected)
    setValue('name', scannedCode)
    lastAutoNameRef.current = scannedCode
    resetScan()
  }, [cardResult]);

  useEffect(() => {
    const detected = detectTagType(tagCode)
    if (detected) setValue('type', detected)

    // Mirror the code into name while the user hasn't edited it manually.
    // If currentName matches what we last auto-filled, we still own it.
    if (currentName === lastAutoNameRef.current) {
      setValue('name', tagCode)
      lastAutoNameRef.current = tagCode
    }

    if (duplicate) setDuplicate(null)
  }, [tagCode]);

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: onTagCreated,
    onError: (error) => {
      if (error?.existing_tag) setDuplicate(error.existing_tag)
    },
    successToastMessage: `Tag agregado`,
    endpoint: `/communities/${communityId}/access/tags`,
  })

  function _submitForm(data) {
    submitForm({
      ...data,
      panel_id: panel.uuid,
    })
  }

  function onUnitSelected(unit) {
    setSelectedUnit(unit)
    setValue("unit_id", unit ? unit.uuid : null)
  }


  if (isScanning) {
    return (
      <Modal onRequestClose={onRequestClose} style={{ modal: `w-80%`, content: `px-6 py-6` }}>
        <View style={tw`items-center`}>
          <Text style={tw`text-secondary font-500 text-xl`}>BUSCANDO</Text>

          <LottieView
            source={require('./loading-animation.json')}
            style={tw`h-40 w-40`}
            autoPlay
          />

          <Text style={tw`text-center text-gray-400 text-lg font-500`}>Acerca la tarjeta al lector</Text>
        </View>
      </Modal>
    )
  }

  const panelOnline = panel?.is_alive

  return (
    <Modal onRequestClose={onRequestClose} style={{ modal: `w-[560px] max-w-[90%]`, content: `px-6 py-6` }}>
      <Text style={tw`text-lg font-600 text-secondary mb-4`}>Agregar tag</Text>

      {!panelOnline && (
        <View style={tw`bg-amber-100 border border-amber-300 rounded-lg px-3 py-2 mb-4`}>
          <Text style={tw`text-amber-800 text-sm`}>Panel desconectado — el tag se sincronizará cuando vuelva en línea.</Text>
        </View>
      )}

      {errorResult && (
        <View style={tw`mb-3`}>
          <Text style={tw`text-center text-base font-500 text-red-400`}>{errorResult}</Text>
        </View>
      )}

      <View style={tw`mb-4`}>
        <Text style={tw`text-gray-500 mb-2`}>Tipo</Text>
        <View style={tw`row gap-3`}>
          {
            typeOptions.map(opt => {
              const isSelected = tagType === opt.value
              return (
                <TouchableOpacity
                  key={opt.value}
                  onPress={() => setValue('type', opt.value)}
                  style={tw.style(
                    `flex-1 py-5 items-center justify-center rounded-lg border`,
                    isSelected ? `bg-[#FBF5FF] border-[#F1DFFB]` : `bg-gray-50 border-gray-200`,
                  )}
                >
                  <Icon name={opt.icon} size={32} />
                  <Text style={tw.style(`mt-2`, isSelected ? `text-secondary font-600` : `text-gray-500`)}>{opt.label}</Text>
                </TouchableOpacity>
              )
            })
          }
        </View>
      </View>

      <View style={tw`mb-4`}>
        <Text style={tw`text-gray-500 mb-2`}>Código</Text>
        <Input
          control={control}
          name='tag_code'
          placeholder='Ej. 0010858185 o 165,44745'
          rules={{ required: true }}
          style={{ subcontainer: tw`border border-gray-200` }}
        />
      </View>

      <View style={tw`mb-4`}>
        <Text style={tw`text-gray-500 mb-2`}>Nombre</Text>
        <Input
          control={control}
          name='name'
          placeholder=''
          rules={{ required: true }}
          style={{ subcontainer: tw`border border-gray-200` }}
        />
      </View>

      <View style={tw`z-20 mb-6`}>
        <Text style={tw`text-gray-500 mb-2`}>Vivienda asignada (opcional)</Text>

        <UnitAutocomplete
          communityId={communityId}
          onUnitSelected={onUnitSelected}
        />
      </View>

      {duplicate && (
        <View style={tw`bg-red-50 border border-red-300 rounded-lg px-3 py-2 mb-4`}>
          <Text style={tw`text-red-700 text-sm font-500`}>Ya existe un tag con ese código</Text>
          <Text style={tw`text-red-700 text-sm`}>
            {duplicate.name ?? 'Sin nombre'}{duplicate.unit?.name ? ` — ${duplicate.unit.name}` : ' — sin asignar'}
          </Text>
        </View>
      )}

      <View style={tw`row justify-between items-center`}>
        <TouchableOpacity
          style={tw.style(`row items-center bg-secondary rounded-full px-4 py-2`, isScanning && `opacity-50`)}
          onPress={scanTag}
          disabled={isScanning}
        >
          <Ionicons name='scan-outline' size={18} color={tw.color('white')} />
          <Text style={tw`ml-2 text-white text-base`}>Escanear</Text>
        </TouchableOpacity>

        <IconButton
          disabled={isSubmitting}
          title={isSubmitting ? 'Agregando' : 'Agregar tag'}
          onPress={handleSubmit(_submitForm)}
        />
      </View>
    </Modal>
  )
}
